import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, X, ChevronLeft, Loader2, Trophy, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuizPlayPage() {
  const { slug } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API}/public/quizzes/${slug}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setQuiz(d))
      .finally(() => setLoading(false));
  }, [slug]);

  const submit = async () => {
    if (!quiz) return;
    const arr = quiz.questions.map((_, i) => answers[i] ?? -1);
    if (arr.some(a => a === -1)) {
      alert('Molimo odgovorite na sva pitanja.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/public/quizzes/${slug}/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: arr }),
      });
      const data = await r.json();
      setResult(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch { alert('Greška pri slanju.'); } finally { setSubmitting(false); }
  };

  const reset = () => { setResult(null); setAnswers({}); };

  if (loading) return <div className="container mx-auto max-w-3xl px-4 py-16 text-center"><Loader2 className="animate-spin mx-auto text-red-500" /></div>;
  if (!quiz) return <div className="container mx-auto max-w-3xl px-4 py-16 text-center text-gray-500">Kviz nije pronađen. <Link to="/kvizovi" className="text-red-600">Natrag na popis</Link></div>;

  return (
    <div className="container mx-auto max-w-3xl px-4 py-8" data-testid="quiz-play-page">
      <Link to="/kvizovi" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-red-600 mb-4">
        <ChevronLeft size={14} /> Svi kvizovi
      </Link>
      <h1 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-gray-100">{quiz.title}</h1>
      {quiz.description && <p className="text-gray-600 dark:text-gray-400 mt-2">{quiz.description}</p>}

      {result ? (
        <div className="mt-8 p-8 rounded-xl border-2 border-red-500/40 bg-red-500/5" data-testid="quiz-result">
          <div className="flex items-center gap-4">
            <Trophy size={48} className="text-red-500" />
            <div>
              <div className="text-5xl font-black text-red-600">{result.score}%</div>
              <div className="text-sm text-gray-500">{result.correct} od {result.total} točno</div>
            </div>
          </div>
          <div className="mt-6 space-y-3">
            {result.details.map((d, i) => (
              <div key={i} className={`p-3 rounded-lg border ${d.is_correct ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-red-500/40 bg-red-500/5'}`}>
                <div className="flex items-start gap-2">
                  {d.is_correct ? <Check size={18} className="text-emerald-500 shrink-0 mt-0.5" /> : <X size={18} className="text-red-500 shrink-0 mt-0.5" />}
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-gray-900 dark:text-gray-100">{i + 1}. {d.question}</div>
                    {d.explanation && <div className="text-gray-500 dark:text-gray-400 mt-1 text-xs">{d.explanation}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button onClick={reset} data-testid="quiz-retry-btn" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold">
            <RefreshCw size={16} /> Pokušaj ponovno
          </button>
        </div>
      ) : (
        <div className="mt-8 space-y-5">
          {quiz.questions.map((q, qi) => (
            <div key={qi} className="p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0f172a]" data-testid={`quiz-question-${qi}`}>
              <div className="text-sm text-gray-400 font-mono">{qi + 1} / {quiz.questions.length}</div>
              <div className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-1">{q.question}</div>
              <div className="mt-3 space-y-2">
                {q.options.map((o, oi) => (
                  <label key={oi} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${answers[qi] === oi ? 'border-red-500 bg-red-500/10' : 'border-gray-200 dark:border-gray-800 hover:border-red-500/50'}`}>
                    <input
                      type="radio"
                      name={`q-${qi}`}
                      checked={answers[qi] === oi}
                      onChange={() => setAnswers(a => ({ ...a, [qi]: oi }))}
                      data-testid={`quiz-option-${qi}-${oi}`}
                      className="accent-red-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">{o.text}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <button onClick={submit} disabled={submitting} data-testid="quiz-submit-btn"
                  className="w-full py-3 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold">
            {submitting ? 'Slanje…' : 'Predaj odgovore'}
          </button>
        </div>
      )}
    </div>
  );
}
